import { Button } from "@/components/ui/button"
import { ProfileDeductions_QueryFragmentFragment } from "@/generated/graphql"

import { cn } from "@/lib/utils"
import { useCurrentOrganization } from "@/novus/hooks/useCurrentOrganization"
import { addDeductionPath } from "@/novus/novus-paths"
import gql from "graphql-tag"
import { default as React, useState } from "react"
import { Link } from "react-router-dom"
import DeductionsListItem from "./DeductionListItem"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

export const ProfileDeduction_Fragment = gql(`
  fragment ProfileDeductionFragment on Deduction {
    id
    amountCents
    fiveTwoNineAccount {
      ...ProfileFiveTwoNineAccountFragment
    }
  }
`)

export const ProfileDeductions_QueryFragment = gql(`
  fragment ProfileDeductions_QueryFragment on Query {
    deductions(userId: $userId, after: $deductionsAfter, first: 6) {
      edges {
        node {
          ...ProfileDeductionFragment
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
`)

const DeductionsList = ({
  data,
  loading,
  fetchMore,
}: {
  data: ProfileDeductions_QueryFragmentFragment["deductions"]
  loading: boolean
  fetchMore: (after: string) => void
}) => {
  const { organization } = useCurrentOrganization()
  const deductions = data?.edges?.map((edge) => edge.node!).filter(Boolean)
  const endCursor = data.pageInfo.endCursor
  const hasNextPage = data.pageInfo.hasNextPage
  const [showPayrollLockedInfo, setShowPayrollLockedInfo] = useState(false)

  const formatOrdinalDate = (day: number) => {
    const suffix = ["th", "st", "nd", "rd"]
    const v = day % 100
    return day + (suffix[(v - 20) % 10] || suffix[v] || suffix[0])
  }

  const getPayrollInfoCopy = () => {
    if (!organization?.deductionRepeatDay) return null

    const currentDate = formatOrdinalDate(organization.deductionRepeatDay)
    const nextDate = formatOrdinalDate(organization.deductionRepeatDay + 1)

    return {
      currentDate,
      nextDate,
    }
  }

  const payrollInfo = getPayrollInfoCopy()

  const handlePayrollLockedClick = () => {
    setShowPayrollLockedInfo(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      handlePayrollLockedClick()
    }
  }

  return (
    <div>
      {deductions.length === 0 ? (
        <div
          className={cn("flex flex-col justify-between gap-10", "md:flex-row")}
        >
          <div
            className={cn(
              "text-2xl text-textPrimary text-center",
              "md:text-left md:max-w-[280px]",
            )}
          >
            Add paycheck-deducted contributions to your 529 accounts
          </div>
          <div className="flex items-center justify-center">
            {organization?.payrollLocked ? (
              <div
                onClick={handlePayrollLockedClick}
                onKeyDown={handleKeyDown}
                className="inline-block"
                role="button"
                tabIndex={0}
              >
                <Button size="md" disabled={true}>
                  Add Deduction
                </Button>
              </div>
            ) : (
              <Button asChild={true} size="md">
                <Link to={addDeductionPath.pattern}>Add Deduction</Link>
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {deductions.map((deduction) => (
            <DeductionsListItem key={deduction.id} deduction={deduction} />
          ))}
          <div className="flex items-center justify-between">
            <div>
              {hasNextPage && endCursor && (
                <button
                  onClick={() => fetchMore(endCursor)}
                  disabled={loading}
                  className="text-neutral-400"
                >
                  View More
                </button>
              )}
            </div>
            {organization?.payrollLocked ? (
              <div
                onClick={handlePayrollLockedClick}
                onKeyDown={handleKeyDown}
                className="inline-block"
                role="button"
                tabIndex={0}
              >
                <Button size="md" disabled={true}>
                  Add Deduction
                </Button>
              </div>
            ) : (
              <Button asChild={true} size="md">
                <Link to={addDeductionPath.pattern}>Add Deduction</Link>
              </Button>
            )}
          </div>
        </div>
      )}

      <Dialog
        open={showPayrollLockedInfo}
        onOpenChange={setShowPayrollLockedInfo}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deduction Enrollment Closed</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {payrollInfo && (
              <>
                <p>
                  529 deduction enrollment for this month is closed until the{" "}
                  <strong>{payrollInfo.currentDate}</strong> of the month.
                </p>
                <p>
                  You are able to opt in and manage your 529 deductions from the{" "}
                  <strong>{payrollInfo.nextDate}</strong> of the month through
                  the cutoff date of next month. Please check back during this
                  time to manage your deductions.
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default DeductionsList
